import React, { useContext, useState, useEffect, useCallback, useRef } from "react";
import { Container } from "@material-ui/core";
import { useNavigate } from "react-router";
import { GetlifeContext } from "../../../context/GetLifeContext.context";
import { I18nContext } from "../../../context/i18n.context";
import { GetLifeTrackerContext } from "../../../context/GetLifeTracker.context";
import { FormBuilderProvider } from "../../../context/FormBuilder.context";
import {
  trackQuestionAnswered,
  trackQuestionViewed,
  trackSubmissionError,
} from "../../../helpers/Tracking";
import HttpLeadRepository from "../../../api/request/Lead/Lead.service";
import FormBuilder from "../../../components/Components/FormBuilder/FormBuilder";
import FormButton from "../../../components/Components/FormBuilder/FormButtons";
import { useQuestionTree } from "./hooks";
import { QuestionTreeResponse, Question } from "./types";
import { QuestionsSummary } from "./components";
import "../css/questions.scss";
import { DOMAIN } from "../../../constants/Global";
import { AnswersMap } from "./types";
import DowngradedTraitsModal from "../../../components/DowngradedTraitsModal/DowngradedTraitsModal";
import HealthDataConsentGate from "../../../components/HealthDataConsent";
import {
  hasHealthConsentInSession,
  isConsentCaptureEnabled,
} from "../../../utils/consentCapture";
import { isNativeMode } from "../../../native/nativeMode";
import { adaptNativeMedicalTree } from "./adapters/adaptNativeMedicalTree";
import { summaryAnswersFromMap } from "./adapters/summaryAnswers";
import { isRejectedVerdict } from "./adapters/nativeVerdict";
import { imcFromHeightWeight, patchOrganicOffer } from "../../../native/organicOffer";
import {
  QuestionTreeErrorKindT,
  QuestionTreeErrorPanel,
  questionTreeErrorKind,
} from "../QuestionTreeError";

/**
 * Obtiene todos los valores seleccionados en las respuestas anteriores
 */
const getAllSelectedValues = (answers: AnswersMap): string[] => {
  const selectedValues: string[] = [];

  Object.values(answers).forEach((answerValue) => {
    if (answerValue && answerValue !== "none") {
      // Asegurar que answerValue es un string antes de hacer split
      const stringValue = typeof answerValue === "string"
        ? answerValue
        : Array.isArray(answerValue)
          ? (answerValue as string[]).join(", ")
          : String(answerValue);

      // Las respuestas pueden ser valores separados por coma
      const values = stringValue.split(",").map((v) => v.trim());
      values.forEach((value) => {
        if (value && !selectedValues.includes(value)) {
          selectedValues.push(value);
        }
      });
    }
  });

  return selectedValues;
};

/**
 * Adapta una pregunta del árbol al formato que espera FormBuilder
 * Para other_options, pasa los valores ya seleccionados para que aparezcan deshabilitados
 */
const adaptQuestionForFormBuilder = (
  question: Question,
  selectedValues: string[] = []
) => {
  const isOtherOptionsQuestion = question.name === "illnesses.axa.v6.other_options";

  // Filtrar los valores seleccionados que coinciden con opciones de esta pregunta
  const previousIllnessesAnswered = isOtherOptionsQuestion
    ? question.options
        ?.filter((opt) => selectedValues.includes(opt.value))
        .map((opt) => ({ value: opt.value, label: opt.label })) || []
    : undefined;

  return {
    questionId: question.id,
    name: question.name,
    type: question.type,
    content: question.content,
    subtitle: question.subtitle || "",
    tooltip: question.tooltip || "",
    componentName: question.component_name || "none",
    field: question.field,
    required: question.required,
    answers: question.options?.map((opt) => ({
      value: opt.value,
      label: opt.label,
    })) || [],
    // Para QuestionSelector: valores ya seleccionados que aparecerán deshabilitados
    previousIllnessesAnswered,
  };
};

const MedicalQuestionsV2: React.FC = () => {
  const navigate = useNavigate();

  // Contextos
  const {
    leadId,
    token,
    loading: globalLoading,
    setLoading,
    setProgressBar,
  } = useContext(GetlifeContext);

  const {
    state: { translate },
  } = useContext(I18nContext);

  const { handleTrackerQuestion } = useContext(GetLifeTrackerContext);

  // Estado local
  const [treeData, setTreeData] = useState<QuestionTreeResponse | null>(null);
  const [isDisplayed, setIsDisplayed] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [downgradedData, setDowngradedData] = useState<{
    removedTraits: string[];
    capitalsIfApplied: Record<string, number>;
  } | null>(null);
  // Organic native (F5b): el confirm native manda TODAS las respuestas acumuladas
  // (el hook las expone, pero se declara más abajo) → se leen por ref al confirmar.
  const answersRef = useRef<AnswersMap>({});
  // Consentimiento explícito de datos de salud (RGPD): inerte sin REACT_APP_CONSENT_CAPTURE
  const [healthConsentGranted, setHealthConsentGranted] = useState<boolean>(
    () => !isConsentCaptureEnabled() || hasHealthConsentInSession(leadId)
  );

  // 503/502 del árbol o de los confirm: se pinta un aviso en la página (nunca
  // navigate("/"), que volvía a mandar aquí y repetía la petición en bucle).
  const [treeError, setTreeError] = useState<QuestionTreeErrorKindT | null>(null);
  const [isRetrying, setIsRetrying] = useState(false);

  const leadRepository = new HttpLeadRepository(leadId, token);

  /** true si el error es del árbol (503/502) y ya se está enseñando el aviso. */
  const showTreeErrorIfAny = (error: unknown): boolean => {
    const kind = questionTreeErrorKind(error);
    if (null === kind) return false;
    setTreeError(kind);
    return true;
  };

  const loadQuestionTree = async () => {
    if (!leadId || !token) return;

    setLoading(true);
    try {
      const response = isNativeMode()
        ? adaptNativeMedicalTree(
            await leadRepository.getMedicalTreeNative(),
            translate
          )
        : await leadRepository.getQuestionStep();
      setTreeError(null);
      setTreeData(response);
    } catch (error) {
      trackSubmissionError(error);
      console.error("Error loading question tree:", error);
      if (!showTreeErrorIfAny(error)) {
        navigate("/");
      }
    } finally {
      setLoading(false);
    }
  };

  // Reintento a mano (solo 503): vuelve a pedir el árbol; con el árbol ya
  // contestado el hook enseña el resumen y el cliente puede volver a confirmar.
  const retryQuestionTree = async () => {
    setIsRetrying(true);
    try {
      await loadQuestionTree();
    } finally {
      setIsRetrying(false);
    }
  };

  // Cargar el árbol de preguntas al montar
  useEffect(() => {
    loadQuestionTree();
  }, [leadId, token]);

  // Callback para guardar respuesta en el servidor
  const handleSaveAnswer = useCallback(
    async (questionId: number, questionName: string, value: string) => {
      try {
        // Native (F5b): no se guarda respuesta a respuesta — el hook las acumula
        // y se mandan todas en el confirm. En monolito sí se persiste cada una.
        if (!isNativeMode()) {
          await leadRepository.saveQuestion({
            answer: value,
            questionId,
          });
        }

        // Contestada (también «ninguna»): nunca se manda la respuesta.
        trackQuestionAnswered(questionId, "medical");

        // Tracking
        handleTrackerQuestion({
          type: "FS",
          event: "Question Answered",
          data: {
            questionId,
            stack: "MedicalV2",
            leadId,
            page: window.location.href,
            platform: "App",
            answerId: value,
            answerString: value,
          },
        });

        handleTrackerQuestion({
          type: "GA",
          data: {
            event: "virtualPageview",
            questionId,
            stack: "MedicalV2",
            leadId,
            answers: value,
          },
        });
      } catch (error) {
        trackSubmissionError(error);
        console.error("Error saving answer:", error);
        throw error;
      }
    },
    [leadId, leadRepository, handleTrackerQuestion]
  );

  // Callback para recargar datos del backend cuando se completan las preguntas
  const handleQuestionsCompleted = useCallback(async () => {
    // Native (F5b): las respuestas viven client-side en el hook; no hay recarga
    // desde el servidor (recargar el árbol native re-sembraría answers vacías).
    if (isNativeMode()) return;
    try {
      const response = await leadRepository.getQuestionStep();
      setTreeData(response);
    } catch (error) {
      console.error("Error reloading question tree:", error);
      if (showTreeErrorIfAny(error)) {
        trackSubmissionError(error);
      }
    }
  }, [leadRepository]);

  // Callback cuando el usuario confirma el resumen y quiere continuar
  const handleContinue = useCallback(async () => {
    setLoading(true);
    try {
      // Native (F5b): confirma con TODAS las respuestas acumuladas (leídas por
      // ref). AGL devuelve la decisión de UW en el 200 (no 409/424); sin
      // downgrade ni telesuscripción. REJECTED (enfermedad marcada / fuera de
      // reglas) → no asegurable; ACCEPTED → upsell.
      if (isNativeMode()) {
        // El imc real (altura/peso del cuestionario) se guarda para que el
        // upsell re-tarifique con el MISMO imc que el quote, no el fallback.
        const imc = imcFromHeightWeight(
          answersRef.current["LeadHealth::height"],
          answersRef.current["LeadHealth::weight"]
        );
        if (imc) {
          patchOrganicOffer({ imc });
        }
        // Product-questions plegadas: la fecha de efecto elegida la usará el
        // select del upsell (materializa el fee con esa decorrenza); la intención
        // se pinta en la confirmación. ms-agl ignora estos fields en el confirm.
        const chosenEffectDate = answersRef.current["Lead::effectDate"];
        const chosenIntention = answersRef.current["Lead::intention"];
        patchOrganicOffer({
          ...("string" === typeof chosenEffectDate && "" !== chosenEffectDate
            ? { effectDate: chosenEffectDate }
            : {}),
          ...("string" === typeof chosenIntention && "" !== chosenIntention
            ? { intention: chosenIntention }
            : {}),
        });
        const r = await leadRepository.confirmQuestionnaireNative(answersRef.current);
        if (isRejectedVerdict(r?.decision)) {
          navigate("/notcoverage");
        } else {
          // Upsell native = PaymentGatewayV2 (NativeUpsell), en /paymentNew.
          navigate("/paymentNew/1");
        }
        return;
      }
      const response = await leadRepository.confirmQuestions();
      if (response?.downgraded === true) {
        setDowngradedData({
          removedTraits: response.removedTraits ?? [],
          capitalsIfApplied: response.capitalsIfApplied ?? {},
        });
        return;
      }
      navigate("/payment/1");
    } catch (error: unknown) {
      // Manejar diferentes códigos de error
      const status = (error as { status?: number })?.status;
      if (status === 409) {
        navigate("/telesubscription");
      } else if (status === 424) {
        navigate("/notcoverage");
      } else {
        trackSubmissionError(error);
        console.error("Error completing questions:", error);
        // Native: las respuestas viven solo en el hook; el reintento recarga el
        // árbol y las perdería. Ahí se queda en el resumen, como hasta ahora.
        if (!isNativeMode()) {
          showTreeErrorIfAny(error);
        }
      }
    } finally {
      setLoading(false);
    }
  }, [leadRepository, navigate, setLoading]);

  const handleDowngradedAccept = useCallback(async () => {
    if (!downgradedData) return;
    setLoading(true);
    try {
      await leadRepository.calculateMultiTraitPrices(
        leadId,
        downgradedData.capitalsIfApplied
      );
      await leadRepository.confirmQuestions();
      setDowngradedData(null);
      navigate("/payment/1");
    } catch (error: unknown) {
      const status = (error as { status?: number })?.status;
      setDowngradedData(null);
      if (status === 409) {
        navigate("/telesubscription");
      } else if (status === 424) {
        navigate("/notcoverage");
      } else {
        trackSubmissionError(error);
        console.error("Error applying downgraded traits:", error);
        showTreeErrorIfAny(error);
      }
    } finally {
      setLoading(false);
    }
  }, [downgradedData, leadId, leadRepository, navigate, setLoading]);

  const handleDowngradedCancel = useCallback(async () => {
    setLoading(true);
    try {
      await leadRepository.forceConfirmQuestions();
      setDowngradedData(null);
      navigate("/payment/1");
    } catch (error: unknown) {
      const status = (error as { status?: number })?.status;
      setDowngradedData(null);
      if (status === 409) {
        navigate("/telesubscription");
      } else if (status === 424) {
        navigate("/notcoverage");
      } else {
        trackSubmissionError(error);
        console.error("Error force-confirming questions:", error);
        showTreeErrorIfAny(error);
      }
    } finally {
      setLoading(false);
    }
  }, [leadRepository, navigate, setLoading]);

  // Hook principal del árbol de preguntas
  const {
    currentQuestion,
    answers,
    questions,
    isFirstQuestion,
    isCompleted,
    progress,
    setAnswer,
    goToNext,
    goToPrevious,
    goToQuestion,
    setIsCompleted,
    getCurrentAnswer,
    getVisibleQuestions,
  } = useQuestionTree({
    treeData,
    onSaveAnswer: handleSaveAnswer,
    // Native: el árbol es ESTÁTICO (no hay recarga dinámica de preguntas). Pasar
    // undefined hace que el hook complete (setIsCompleted(true)) tras la última
    // pregunta; con el callback no-op nativo se quedaba colgado esperando una
    // recarga de treeData que nunca llega.
    onQuestionsCompleted: isNativeMode() ? undefined : handleQuestionsCompleted,
  });

  // Native (F5b): mantener la ref con las respuestas acumuladas del hook, para
  // que el confirm native (handleContinue, declarado antes del hook) las lea.
  useEffect(() => {
    answersRef.current = answers;
  }, [answers]);

  // Actualizar progress bar global
  useEffect(() => {
    const progressValue = 70 + (progress * 0.2); // 70-90% del total
    setProgressBar(progressValue);
  }, [progress, setProgressBar]);

  // Emit question_viewed every time we show a new question
  useEffect(() => {
    if (!currentQuestion) return;
    trackQuestionViewed(currentQuestion.id, "medical");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentQuestion?.id]);

  const handleGoBack = useCallback(() => {
    goToPrevious();
  }, [goToPrevious]);

  // Manejar cambio de respuesta
  const handleValues = (value: string | string[]) => {
    const stringValue = Array.isArray(value) ? value.join(", ") : value;
    setAnswer(stringValue);
  };

  // Manejar siguiente pregunta con transición que cubre el tiempo de guardado
  const handleNextQuestion = async () => {
    setIsTransitioning(true);
    window.scrollTo(0, 0);

    // Ejecutar guardado y transición en paralelo
    // La transición visual cubre el tiempo de espera del backend
    await Promise.all([
      goToNext(),
      new Promise((resolve) => setTimeout(resolve, 200)), // Mínimo 200ms de transición
    ]);

    // Quitar transición para mostrar la nueva pregunta
    setIsTransitioning(false);
  };

  // Manejar edición de pregunta desde el resumen
  const handleEditQuestion = (questionId: number) => {
    setIsCompleted(false);
    goToQuestion(questionId);
  };

  // Paso previo al cuestionario de salud: sin consentimiento explícito no se continúa
  if (!healthConsentGranted) {
    return (
      <Container className="questions-outerContainer">
        <HealthDataConsentGate
          leadId={leadId}
          token={token}
          translate={translate}
          domain={DOMAIN}
          onConsented={() => setHealthConsentGranted(true)}
        />
      </Container>
    );
  }

  if (null !== treeError) {
    return (
      <Container className="questions-outerContainer">
        <QuestionTreeErrorPanel
          kind={treeError}
          translate={translate}
          onRetry={retryQuestionTree}
          retrying={isRetrying}
        />
      </Container>
    );
  }

  // Si está completado, mostrar resumen
  if (isCompleted) {
    return (
      <Container className="questions-outerContainer">
        <QuestionsSummary
          questions={questions}
          answers={
            // Native: las respuestas viven en el hook (no vienen con el árbol).
            isNativeMode()
              ? summaryAnswersFromMap(getVisibleQuestions(), answers)
              : treeData?.answers || []
          }
          onContinue={handleContinue}
          onEditQuestion={handleEditQuestion}
          translate={translate}
          isLoading={globalLoading}
          locale={DOMAIN === "it" ? "it-IT" : DOMAIN === "pt" ? "pt-PT" : DOMAIN === "fr" ? "fr-FR" : DOMAIN === "en" ? "en-GB" : "es-ES"}
        />
        <DowngradedTraitsModal
          isOpen={downgradedData !== null}
          removedTraits={downgradedData?.removedTraits ?? []}
          capitalsIfApplied={downgradedData?.capitalsIfApplied ?? {}}
          translate={translate}
          onConfirm={handleDowngradedAccept}
          onCancel={handleDowngradedCancel}
        />
      </Container>
    );
  }

  // Si no hay pregunta actual, mostrar loading
  if (!currentQuestion || globalLoading) {
    return null;
  }

  // Obtener valores ya seleccionados para deshabilitar opciones en other_options
  const selectedValues = getAllSelectedValues(answers);

  // Adaptar pregunta para FormBuilder
  const formBuilderQuestion = adaptQuestionForFormBuilder(currentQuestion, selectedValues);

  // Obtener la respuesta actual
  const currentAnswer = getCurrentAnswer() || "";

  return (
    <Container className="questions-outerContainer">
      <div className="questions-outerContainer">
        {currentQuestion && (
          <FormBuilderProvider
            question={formBuilderQuestion}
            answer={currentAnswer}
          >
            <div
              className={`questions-container ${
                isTransitioning ? "questions-transitioning" : "questions-visible"
              }`}
            >
              <div
                className={`questions-innerContainer ${
                  DOMAIN === "it" ? "questions-innerContainer-it" : ""
                }`}
              >
                <FormBuilder
                  key={currentQuestion.id}
                  answer={currentAnswer}
                  capitalLimits={null}
                  chat={false}
                  countryList={null}
                  error={null}
                  handleSubmit={handleNextQuestion}
                  handleValues={handleValues}
                  onBlur={() => {}}
                  personalData={null}
                  question={formBuilderQuestion}
                  callback={(e: string) => {
                    handleValues(e);
                  }}
                  isOneChoinceQuestionRoute={false}
                  isDisplayed={isDisplayed}
                  setIsDisplayed={setIsDisplayed}
                  setValidateQuestionDateInput={() => {}}
                />
              </div>
              <FormButton
                showButton={true}
                isLoading={globalLoading}
                isOneChoinceQuestion={false}
                componentName={currentQuestion.component_name}
                translate={translate}
                handleSubmit={handleNextQuestion}
                question={formBuilderQuestion}
                answer={currentAnswer}
                goBack={handleGoBack}
                customDisabled={false}
                questionStack={[]}
                isSticky={false}
                currentIndex={0}
              />
            </div>
          </FormBuilderProvider>
        )}
      </div>
    </Container>
  );
};

export default MedicalQuestionsV2;
